import { Link, useParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { usePipelineBoard, type Stage } from './api'
import { Dot, money, PipelineFrame, shortDay, STAGE_LABEL, Stat } from './shared'

const OPEN: Stage[] = ['demo_booked', 'demo_done', 'pilot_agreed', 'pilot_running', 'pilot_review', 'contract']

/** C8 — the dashboard on the new stages: how much is where, what it is worth, and what is slipping. */
export function DashboardPage() {
  const { workspaceSlug = '' } = useParams()
  const board = usePipelineBoard()
  return (
    <PipelineFrame title="Pipeline dashboard" description="The same deals as the board, counted. Weighted value uses one chance per stage, so the forecast and the report always agree.">
      {board.error ? (
        <ErrorState error={board.error} onRetry={() => void board.refetch()} />
      ) : board.isPending ? (
        <LoadingState label="Counting the pipeline" />
      ) : !board.data.connected ? (
        <EmptyState title="GoHighLevel is not connected" description="Connect it under Settings → Integrations." />
      ) : (
        (() => {
          const { deals, forecast: fc, hygiene: h } = board.data
          const open = deals.filter((d) => OPEN.includes(d.stage))
          const won = deals.filter((d) => d.stage === 'won').length
          const lost = deals.filter((d) => d.stage === 'lost').length
          const max = Math.max(1, ...OPEN.map((s) => open.filter((d) => d.stage === s).length))
          const maxMonth = Math.max(1, ...(fc?.byMonth.map((m) => m.weighted) ?? [1]))
          const red = open.filter((d) => d.level === 'red')
          const deciding = open.filter((d) => d.facts.decisionAt).sort((a, b) => Date.parse(a.facts.decisionAt!) - Date.parse(b.facts.decisionAt!)).slice(0, 8)
          return (
            <div className="grid gap-5">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Stat label="Open deals" value={open.length} />
                <Stat label="Pilots running" value={open.filter((d) => d.stage === 'pilot_running').length} />
                {fc ? <Stat label="Open value" value={money(fc.open)} /> : null}
                {fc ? <Stat label="Weighted forecast" value={money(fc.weighted)} hint="Each deal's value times the chance for its stage" /> : null}
                <Stat label="Won / lost (45 days)" value={`${won} / ${lost}`} hint={won + lost ? `${Math.round((won / (won + lost)) * 100)}% win rate` : undefined} />
                <Stat label="Up to date" value={`${h.cleanPct}%`} tone={h.cleanPct >= 80 ? 'text-success' : h.cleanPct >= 50 ? 'text-warning' : 'text-danger'} hint="Next step set and not late, a value from Pilot agreed, not stale" />
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h2 className="mb-3 text-sm font-semibold">Deals by stage</h2>
                  <ul className="grid gap-2">
                    {OPEN.map((s) => {
                      const here = open.filter((d) => d.stage === s)
                      const row = fc?.byStage.find((x) => x.stage === s)
                      return (
                        <li key={s} className="grid grid-cols-[7.5rem_1fr_auto] items-center gap-3 text-sm">
                          <span className="text-muted-foreground">{STAGE_LABEL[s]}</span>
                          <div className="h-5 overflow-hidden rounded bg-surface-sunken">
                            <div className="flex h-full items-center rounded bg-brand px-1.5 text-tiny font-medium text-primary-foreground" style={{ width: `${Math.max(here.length ? 8 : 0, (here.length / max) * 100)}%` }}>
                              {here.length || ''}
                            </div>
                          </div>
                          <span className="num w-28 text-right text-xs text-muted-foreground">{row ? `${money(row.value)} · ${money(row.weighted)}` : ''}</span>
                        </li>
                      )
                    })}
                  </ul>
                  {fc ? <p className="mt-3 text-xs text-muted-foreground">Value · weighted value. Chances: demo 5–15%, pilot 30–60%, contract 85%.</p> : null}
                </section>

                <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h2 className="mb-3 text-sm font-semibold">Weighted forecast by expected close</h2>
                  {fc && fc.byMonth.length ? (
                    <div className="flex h-44 items-end gap-3">
                      {fc.byMonth.slice(0, 8).map((m) => (
                        <div key={m.month} className="flex flex-1 flex-col items-center gap-1">
                          <span className="num text-tiny text-muted-foreground">{money(m.weighted)}</span>
                          <div className="w-full rounded-t bg-chart-1" style={{ height: `${Math.max(4, (m.weighted / maxMonth) * 130)}px` }} />
                          <span className="text-tiny text-muted-foreground">{new Date(`${m.month}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{fc ? 'No open deal has a decision date or an expected close yet. Add decision dates to the pilots and they show here.' : 'Values are visible to CRM members only.'}</p>
                  )}
                </section>
              </div>

              <div className="grid gap-5 lg:grid-cols-2">
                <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h2 className="mb-3 text-sm font-semibold">Needs action now <span className="num font-normal text-muted-foreground">{red.length}</span></h2>
                  {red.length ? (
                    <ul className="grid gap-2">
                      {red.slice(0, 10).map((d) => (
                        <li key={d.id} className="flex items-start gap-2 text-sm">
                          <Dot level="red" className="mt-1.5" />
                          <div className="min-w-0">
                            <Link to={`${paths.pipeline(workspaceSlug)}?deal=${encodeURIComponent(d.id)}`} className="font-medium hover:text-brand">{d.company.name}</Link>
                            <span className="text-muted-foreground"> · {STAGE_LABEL[d.stage]} · {d.owner.name ?? 'No owner'}</span>
                            <div className="text-xs text-danger">{d.reasons.join(' · ')}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">Nothing red.</p>
                  )}
                </section>
                <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
                  <h2 className="mb-3 text-sm font-semibold">Decisions coming up</h2>
                  {deciding.length ? (
                    <ul className="grid gap-2 text-sm">
                      {deciding.map((d) => (
                        <li key={d.id} className="flex items-center justify-between gap-2">
                          <Link to={`${paths.pipeline(workspaceSlug)}?deal=${encodeURIComponent(d.id)}`} className="truncate hover:text-brand">{d.company.name}</Link>
                          <span className={cn('num shrink-0 text-xs', d.decisionOverdue ? 'font-medium text-danger' : 'text-muted-foreground')}>{shortDay(d.facts.decisionAt)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted-foreground">No decision dates set yet. They are one of the five pilot facts.</p>
                  )}
                </section>
              </div>
            </div>
          )
        })()
      )}
    </PipelineFrame>
  )
}
